import { ArrowRight, Mail } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Spinner from '../shared/Spinner';
import { Button } from '../ui/button';

interface BasePageProps {
	title?: string;
	description?: string;
	buttonText?: string;
	buttonLink?: string;
	isError?: string | boolean;
	is404?: boolean;
	isLoading?: boolean;
	showContactButton?: boolean;
}

const BasePage = ({
	title,
	description,
	buttonText,
	buttonLink = '/',
	isError = false,
	is404 = false,
	isLoading = false,
	showContactButton = false
}: BasePageProps): React.JSX.Element => {
	const { t } = useTranslation();
	const [showAlternateMessage, setShowAlternateMessage] = useState(false);
	const [displayTitle, setDisplayTitle] = useState(title);
	const [displayDescription, setDisplayDescription] = useState(description);
	const [displayButtonText, setDisplayButtonText] = useState(
		buttonText || t('common.goToHomepage')
	);
	const [displayButtonLink, setDisplayButtonLink] = useState(buttonLink);

	useEffect(() => {
		if (is404) {
			setDisplayTitle(t('errors.pageNotFound'));
			setDisplayDescription(t('errors.pageNotFoundDescription'));
			setDisplayButtonText(t('common.goToHomepage'));
			setDisplayButtonLink('/');
		}

		if (isError) {
			setDisplayTitle(t('errors.somethingWentWrong'));
			setDisplayDescription(typeof isError === 'string' ? isError : t('errors.unexpectedError'));
			setDisplayButtonText(t('common.goToHomepage'));
			setDisplayButtonLink('/');
		}

		// Handle alternating message for loading state
		let toggleInterval: ReturnType<typeof setInterval> | undefined;
		if (isLoading) {
			toggleInterval = setInterval(() => {
				setShowAlternateMessage((prev) => !prev);
			}, 2000);
		}

		// Cleanup function
		return () => {
			if (toggleInterval) {
				clearInterval(toggleInterval);
			}
		};
	}, [isLoading, is404, isError, t]);

	return (
		<section className="flex grow items-center">
			{isLoading && (
				<div className="container mx-auto px-4 text-center">
					<div className="text-center pb-12">
						<h1 className="text-2xl font-bold mb-10">
							{showAlternateMessage ? t('common.stillWaiting') : displayTitle}
						</h1>
						<Spinner size="lg" variant="primary" className="mx-auto" />
					</div>
				</div>
			)}

			{!isLoading && (
				<div className="container mx-auto px-4 pb-20">
					<div className="lg:w-7/12 mx-auto text-center">
						<h1 className="text-4xl font-bold mb-10">{displayTitle}</h1>
						<p className="text-xl mb-10 leading-loose">{displayDescription}</p>
						<div className="flex flex-col sm:flex-row gap-3 justify-center">
							{showContactButton && (
								<Button
									type="button"
									variant="outline"
									size="lg"
									className="px-5 py-3 text-base font-medium"
									onClick={() => {
										window.dispatchEvent(new CustomEvent('openContactModal'));
									}}
									icon={<Mail className="w-4 h-4" />}
								>
									{t('common.contactUs')}
								</Button>
							)}
							<Button
								variant="default"
								size="lg"
								className="px-5 py-3 text-base font-medium"
								asChild
							>
								<Link to={displayButtonLink}>
									{displayButtonText}
									<ArrowRight className="w-4 h-4 ml-2" />
								</Link>
							</Button>
						</div>
					</div>
				</div>
			)}
		</section>
	);
};

export default BasePage;
